/**
 * Wie viel noch geht — und zwar erst, wenn es knapp wird.
 *
 * **„Erst kurz vor Schluss."** Ein Zähler, der von Zeichen eins an mitläuft,
 * ist Aufsicht: er steht neben jedem Satz, den jemand schreibt, und zählt ihn
 * mit. Einer, der bei neunzig Prozent erscheint, ist eine Warnung — und die
 * ist genau dann da, wenn sie gebraucht wird.
 *
 * **Gezählt wird in Zeichen, nicht in Bytes.** Der Server prüft beides (siehe
 * `Textgrenzen`), aber die Zahl, die hier steht, muss jemand nachzählen
 * können. „Noch 412 Zeichen" ist eine Auskunft; „noch 1 648 Bytes" ist keine.
 *
 * **Und über der Grenze sagt es das, statt zu kürzen.** Eine stille Kürzung
 * ist eine stille Absage — das ist die Regel des Hauses, und sie gilt in der
 * Oberfläche genauso wie auf dem Server.
 */
export function Restzeichen({
  wert,
  grenze,
  ab = 0.9,
  zusatz,
}: {
  /** Der getippte Text. */
  wert: string
  /** Wie viele Zeichen erlaubt sind. */
  grenze: number
  /**
   * Ab welchem Anteil der Grenze der Zähler erscheint.
   *
   * Als Zahl und nicht fest verdrahtet, weil es Felder gibt, an denen die
   * Grenze knapp ist: bei fünfhundert Zeichen am Spot ist neunzig Prozent
   * noch reichlich, bei achttausend im Beitrag ist es eine ganze Seite.
   */
  ab?: number
  /**
   * Ein Satz, der immer dasteht — etwa „gesichert wird, wenn du das Feld
   * verlässt". Er hängt an dieser Zeile, damit es unter dem Feld eine
   * Hinweiszeile gibt und nicht zwei.
   */
  zusatz?: string
}) {
  const hat = [...wert].length
  const rest = grenze - hat
  const zuViel = rest < 0
  const zeigen = hat >= Math.floor(grenze * ab)

  if (!zeigen) {
    return zusatz === undefined ? null : <span className="feld__hinweis">{zusatz}</span>
  }

  return (
    <span
      className={zuViel ? 'feld__hinweis feld__hinweis--zuviel' : 'feld__hinweis'}
      /* **Nur über der Grenze eine Meldung.** Ein `role="alert"`, das schon
         beim Näherkommen spricht, unterbricht den Vorleser mitten im Satz —
         und das bei jedem weiteren Zeichen. */
      role={zuViel ? 'alert' : undefined}
    >
      {zuViel
        ? `${-rest} Zeichen zu viel — bitte kürzen.`
        : `Noch ${rest} von ${grenze} Zeichen.`}
      {zusatz === undefined ? null : ` · ${zusatz}`}
    </span>
  )
}
